import React, { JSX, useEffect, useState } from "react";
import { useWallet } from "../../../hooks/useWallet";
import { useWalletTransactions } from "../../../hooks/useWalletTransactions";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { PaymentType, renderAmount, renderFee, renderOpaFee, renderRoutingFee, Transaction, TransactionStatus } from "../../../models/Transactions";
import { getTransactionDisplayAmount } from "../../../utils/transactionDisplay";
import Loader from "../components/Loader";
import { dateStringToLocaleDate, dateStringToLocaleHour, formatAmount, formatAmountWithSATS } from "../../../utils/Utils";
import { LayoutWithTopBar } from "../../../components/LayoutWithTopBar";
import { useTheme } from "../../../hooks/useTheme";
import ButtonsInfosTxSenderReceiver from "./components/ButtonsInfosTxSenderReceiver";
import { DialogTransactionRecipients } from "./components/DialogTransactionRecipients";
import { BulkTypes } from "../../../models/BulkPayment";
import { streamService } from "../../../utils/StreamService";
import { useRouter } from "../../../hooks/useRouter";
import mixpanel from "../../../utils/MixPanel";
import { APP_NAME } from "../../../config/app";
import { WalletStatus } from "../../../store/slices/walletSlice";

interface TransactionDetailsProps {
  
}

const TransactionDetails: React.FC<TransactionDetailsProps> = () => {
    const { currencyType, status } = useWallet();
    const { getTransaction, getSearchTransaction, markTransactionAsRead } = useWalletTransactions();
    const { paymentHash } = useParams();
    const [transaction, setTransaction] = useState<Transaction | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const {isMobile, isDarkMode} = useTheme();
    const isSend = !transaction?.amount || transaction.amount < 0;
    const [showDialogRecipients, setShowDialogRecipients] = useState(false);
    const [fullscreenImageUrl, setFullscreenImageUrl] = useState<string | null>(null);
    const {navigateFromFullChannelId} = useRouter();
    
    useEffect(() => {
        if (status === WalletStatus.ACTIVE && paymentHash) {
            getTransaction(paymentHash)
                .then(async (transaction) => {
                    if (!transaction) {
                        return getSearchTransaction(paymentHash);
                    }
                    return transaction;
                })
                .then((transaction) => {
                    if(transaction) {
                        setTransaction(transaction);
                        mixpanel.track('Wallet Transaction Viewed', {
                            Source: 'Wallet',
                            "Transaction ID": transaction.id,
                            "Bulk Transaction ID": transaction.bulkTransactionId ?? "",
                            "Amount MSAT": transaction.amountMsat,
                        });
                    }
                })
                .finally(() => setIsLoading(false));
        }
    }, [paymentHash, getTransaction, getSearchTransaction, status]);

    useEffect(() => {
        if (transaction) {
            if(transaction.read === false) {
                if(location.state?.markAsReadTimeout) {
                    setTimeout(() => {
                        markTransactionAsRead(transaction);
                    }, location.state?.markAsReadTimeout);
                }
                else {
                    markTransactionAsRead(transaction);
                }
            }
        }
    }, [transaction, markTransactionAsRead]);

    const renderTextWithLinks = (text: string): JSX.Element[] => {
        // Improved regex to detect both full URLs and plain domain names
        const urlRegex = /(?<!\S)(https?:\/\/[^\s]+|www\.[^\s]+|\b[a-zA-Z0-9][-a-zA-Z0-9]*(\.[a-zA-Z0-9][-a-zA-Z0-9]*)*\.(com|net|info|app|io|org|ly)\b)(?!\S)/g;        

        // Create an array to hold our result elements
        const result: JSX.Element[] = [];
        
        // Find all matches
        const matches = Array.from(text.matchAll(urlRegex));
        let lastIndex = 0;
        
        // Process each match
        matches.forEach((match, i) => {
            const matchIndex = match.index!;
            const rawMatchText = match[0];
            // Keep trailing sentence punctuation outside the link (e.g. "books. C")
            const matchText = rawMatchText.replace(/[.,;:!?)]+$/g, '');
            const trailingPunctuation = rawMatchText.slice(matchText.length);

            // Add text before the match
            if (matchIndex > lastIndex) {
                result.push(
                    <span key={`text-${i}`}>
                        {text.substring(lastIndex, matchIndex)}
                    </span>
                );
            }

            if (matchText) {
                const href = matchText.startsWith('http') ? matchText : `https://${matchText}`;

                result.push(
                    <a
                        key={`link-${i}`}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline text-brand-orange break-all"
                        onClick={() => mixpanel.track('Wallet Transaction URL Opened', {
                            Source: 'Wallet',
                            "Transaction ID": transaction?.id ?? "",
                            "Bulk Transaction ID": transaction?.bulkTransactionId ?? "",
                            "Amount MSAT": transaction?.amountMsat ?? 0,
                            "URL Opened": href
                        })}
                    >
                        {matchText}
                    </a>
                );
            }

            if (trailingPunctuation) {
                result.push(
                    <span key={`punct-${i}`}>
                        {trailingPunctuation}
                    </span>
                );
            }

            lastIndex = matchIndex + rawMatchText.length;
        });
        
        // Add any remaining text after the last match
        if (lastIndex < text.length) {
            result.push(
                <span key="text-last">
                    {text.substring(lastIndex)}
                </span>
            );
        }
        
        return result;
    };

    const title = () => {
        if(transaction?.customTitle) {
            return transaction.customTitle;
        }

        let titlePayment = transaction?.zapType ?? 'Zap';

        if(titlePayment !== '') {
            titlePayment += isSend ? ' Sent' : ' Received';
        }
        
        return titlePayment;
    }

    const handlerBulkRecipientClick = () => {
        if(
            transaction?.type == PaymentType.bulk && 
            (transaction.bulk?.type === BulkTypes.group || transaction.bulk?.type === BulkTypes.event)
        ) {
            setShowDialogRecipients(true);
        }
    }

    const onClickTapZapMessage = () => {
        navigateFromFullChannelId(transaction?.additionalInfos?.channelId ?? '', undefined, transaction?.additionalInfos?.messageId);
    }

    const renderTransactionImage = (imageUrl?: string) => {
        if(!imageUrl) {
            return null;
        }

        return (
            <div className="flex flex-col items-center">
                <button
                    type="button"
                    className="w-80 max-w-full aspect-video overflow-hidden rounded-xl cursor-pointer"
                    onClick={() => setFullscreenImageUrl(imageUrl)}
                >
                    <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                </button>
            </div>
        );
    };

    const renderImageFullscreenModal = () => {
        if(!fullscreenImageUrl) {
            return null;
        }

        return (
            <div
                className="fixed inset-0 z-50 bg-black/90 p-4 flex items-center justify-center"
                onClick={() => setFullscreenImageUrl(null)}
            >
                <img
                    src={fullscreenImageUrl}
                    alt=""
                    className="max-w-full max-h-full object-contain"
                    onClick={(e) => e.stopPropagation()}
                />
                <button
                    type="button"
                    className="absolute top-4 right-4 text-white text-3xl leading-none"
                    onClick={() => setFullscreenImageUrl(null)}
                    aria-label="Close fullscreen image"
                >
                    ×
                </button>
            </div>
        );
    };

    const renderPendingStatus = () => {
        const pendingTitle = isSend
            ? 'Sending...'
            : transaction?.btcTxId
                ? 'Pending Deposit'
                : 'Receiving...';

        return (
            <>
                <div className="flex flex-col items-center">
                    <Loader />
                </div>

                <div className="text-center text-4xl">
                    {pendingTitle}
                </div>

                {
                    isSend ? (
                        <div className="bg-brand-bg-5 px-4 py-3 rounded-lg flex items-center gap-2">
                            <span className="material-icons">info</span>
                            <span>Zap in progress. You may leave this page.</span>
                        </div>
                    ) : null
                }
            </>
        );
    };

    const renderInside = () => {
        if(isLoading) return <Loader />;

        if(transaction)
        {            
            const showTemplateSuccess = location.state?.template === 'success' && transaction.type !== PaymentType.bulk;

            if(showTemplateSuccess) {
                return (
                    <>
                        <div className="flex flex-col justify-center items-center flex-1 gap-8 p-4">
                            {
                                transaction.status === TransactionStatus.PENDING ? (
                                    renderPendingStatus()
                                ) : (
                                    <>
                                        <div className="flex flex-col items-center">
                                            <img src={"/images/payment_success.svg"} alt="" className="w-60 h-60" />
                                        </div>

                                        <div className="flex flex-col items-center">
                                            <div className={`font-semibold text-3xl`}>
                                                {title()}
                                            </div>
                                        </div>
                                    </>
                                )
                            }

                            

                            <ButtonsInfosTxSenderReceiver 
                                transaction={transaction} 
                                onClickBulkRecipient={handlerBulkRecipientClick}
                            />
        
                            <div className="flex flex-col items-center">
                                <div className={`font-semibold text-3xl`}>
                                    {formatAmountWithSATS(Math.abs(getTransactionDisplayAmount(transaction)))}
                                </div>
                            </div>
                            
                            {
                                transaction.comment && (
                                    <div className="">
                                        <div className="max-w-[500px] text-center break-words [overflow-wrap:anywhere]">{renderTextWithLinks(transaction.comment)}</div>
                                    </div>
                                )
                            }

                            {
                                renderTransactionImage(transaction.imageUrl)
                            }
                        </div>
        
                        <div className="flex flex-col items-center p-4 mb-2">
                        {
                            isDarkMode ?
                                <img src={"/images/logo_text.svg"} alt="" className="w-[150px]" />
                            :
                                <img src={"/images/logo_text_light.svg"} alt="" className="w-[150px]" />
                        }
                        </div>
                    </>
                );
            }
            else {
                return (
                    <>
                        <div className="flex flex-col justify-center items-center flex-1 gap-8 p-4 max-w-[100vw]">
                            {
                                transaction.status === TransactionStatus.PENDING ? (
                                    renderPendingStatus()
                                ) : (
                                    <>
                                        <div className="flex flex-col items-center">
                                            <img src={"/images/payment_success.png"} alt="" className="w-40 h-40 rounded-full" />
                                        </div>

                                        <div className="text-center text-4xl">
                                            {title()}
                                        </div>
                                    </>
                                )
                            }

                            {
                                transaction?.additionalInfos?.messageTapZap && (
                                    <div className="flex flex-col items-center">
                                        <div className="max-w-[500px] text-center break-words [overflow-wrap:anywhere] cursor-pointer" onClick={onClickTapZapMessage}>
                                            {transaction?.additionalInfos?.messageTapZap}
                                        </div>
                                    </div>
                                )
                            }
                        
                            <ButtonsInfosTxSenderReceiver 
                                transaction={transaction} 
                                onClickBulkRecipient={handlerBulkRecipientClick}
                            />

                            <div className="flex flex-col items-center">
                                <div className={`font-semibold text-3xl ${isSend ? '' : 'text-brand-green'}`}>
                                    {renderAmount(transaction, currencyType)}
                                </div>
                                {
                                    transaction.btcTxId && (
                                        <a
                                            href={`https://mempool.space/tx/${transaction.btcTxId}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 mt-3 px-4 py-2 rounded-full bg-brand-bg-4 hover:bg-brand-bg-3 transition-colors text-sm"
                                        >
                                            <span>Open in Transaction Explorer</span>
                                            <i className="material-icons text-[18px]">open_in_new</i>
                                        </a>
                                    )
                                }
                                {
                                    isSend && (
                                        <div className="flex flex-col items-center mt-2">
                                            {
                                                transaction?.opaFee && transaction?.routingFee ? (
                                                    <>
                                                        <div className="text-brand-color-2 text-sm">{APP_NAME} Fees : {renderOpaFee(transaction, currencyType)}</div>
                                                        <div className="text-brand-color-2 text-sm">Routing Fees : {renderRoutingFee(transaction, currencyType)}</div>
                                                    </>
                                                ) : (
                                                    Math.abs(transaction.fee) !== 0 && (
                                                        <div className="text-brand-color-2 text-sm">Fee : {renderFee(transaction, currencyType)}</div>
                                                    )
                                                )
                                            }
                                        </div>
                                    )
                                }
                                <div className="flex flex-col items-center text-brand-color-2 text-sm mt-2">
                                    <div>{dateStringToLocaleDate(transaction.time)} {dateStringToLocaleHour(transaction.time)}</div>
                                </div>
                            </div>

                            {
                                transaction.status === TransactionStatus.PENDING && transaction.type === PaymentType.bulk && (
                                    <div className="flex flex-col items-center hidden">
                                        <div className="font-semibold text-xl text-center mb-2">{transaction?.zapType ?? 'Zap'}:</div>
                                        <div className="text-center text-brand-color-2">{transaction.bulk?.nbSendedSuccess && formatAmount(transaction.bulk?.nbSendedSuccess)} / {transaction.bulk?.nbToSend && formatAmount(transaction.bulk?.nbToSend)}</div>
                                        {(transaction.bulk?.nbSendedError ?? 0) > 0 && <div className="text-center text-brand-color-2">{transaction.bulk?.nbSendedError && formatAmount(transaction.bulk?.nbSendedError)} errors</div>}
                                    </div>
                                )
                            }
                            
                            {
                                /* transaction.bulk?.nbToSend && transaction.bulk?.nbToSend > 1 ? (
                                    <div className="flex flex-col items-center">
                                        <div className="text-center text-brand-color-2">
                                            {transaction.bulk?.nbSendedSuccess} sent / {formatAmount(transaction.bulk?.nbToSend)} recipients 
                                            {
                                                transaction.bulk?.nbSendedError && transaction.bulk?.nbSendedError > 0 ? ` (${formatAmount(transaction.bulk?.nbSendedError)} errors)` : ''
                                            }
                                        </div>
                                    </div>
                                ) : null */
                            }
                            {
                                transaction.comment && (
                                    <div className="">
                                        <div className="max-w-[500px] text-center break-words [overflow-wrap:anywhere]">{renderTextWithLinks(transaction.comment)}</div>
                                    </div>
                                )
                            }
                            {
                                renderTransactionImage(transaction.imageUrl)
                            }
                        </div>
                        <div className="flex flex-col items-center p-4 mb-2">
                        {
                            isDarkMode ?
                                <img src={"/images/logo_text.svg"} alt="" className="w-[150px]" />
                            :
                                <img src={"/images/logo_text_light.svg"} alt="" className="w-[150px]" />
                        }
                        </div>
                    </>
                );
            }
        }
        else {
            return null;
        }
    }

    const onClickPrevArrow = () => {
        if(isMobile)
            return () => navigate(`/home/wallet`);

        return undefined;
    }        

    return <LayoutWithTopBar
        breadcrumbs={[
            { title: 'Wallet', onClick: () => navigate('/home/wallet') },
            { title: 'Transaction' }
        ]}
        onClickPrevArrow={onClickPrevArrow()}
    >
        {renderInside()}
        {showDialogRecipients && transaction && <DialogTransactionRecipients transaction={transaction} onClose={() => setShowDialogRecipients(false)} />}
        {renderImageFullscreenModal()}
    </LayoutWithTopBar>
}

export default TransactionDetails;